import React from 'react';
import { useTranslation } from 'react-i18next';
import { FarmBenchmarkMetric } from '../../models/farm-benchmark';
import { AnchorRect, useAnchorRect } from '../../hooks/useAnchorRect';
import { HoverCard } from '../../elements/ui/hover-card';
import { farmBenchmarkColors as c, FarmBenchmarkStanding } from './colors';
import { formatValue, metricLabel } from './format';

/** Headroom over the largest figure, so the highest mark never touches the right edge. */
const HEADROOM = 1.1;

const percent = (value: number, max: number) => `${Math.min(100, Math.max(0, (value / max) * 100))}%`;

const BarTooltip = ({
  metric,
  standing,
  anchor
}: {
  metric: FarmBenchmarkMetric;
  standing: FarmBenchmarkStanding;
  anchor: AnchorRect;
}) => {
  const { t } = useTranslation();
  const value = (figure: number | null) =>
    figure === null ? t('f-benchmark-no-data') : formatValue(figure, metric.unit);

  const rows: [string, string][] = [
    [t('f-benchmark-legend-farm'), value(metric.farmValue)],
    [
      metric.metric === 'Mortality' ? t('f-benchmark-tooltip-births') : t('f-benchmark-tooltip-animals'),
      metric.farmSampleSize.toLocaleString()
    ],
    [t('f-benchmark-legend-average'), value(metric.othersAverage)],
    [
      t('f-benchmark-legend-range'),
      metric.othersMin === null || metric.othersMax === null
        ? t('f-benchmark-no-data')
        : `${formatValue(metric.othersMin, metric.unit)} - ${formatValue(metric.othersMax, metric.unit)}`
    ],
    [t('f-benchmark-tooltip-other-farms'), metric.otherFarmsCount.toLocaleString()]
  ];

  return (
    <HoverCard anchor={anchor} className="min-w-64">
      <p className="mb-1.5 font-medium text-gray-700">{metricLabel(metric.metric, t)}</p>

      <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
        {rows.map(([label, figure]) => (
          <React.Fragment key={label}>
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-medium tabular-nums text-gray-900">{figure}</span>
          </React.Fragment>
        ))}
      </div>

      {standing !== 'neutral' && (
        <p className="mt-2 flex items-center gap-1.5 border-t border-gray-100 pt-1.5 text-gray-500">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: c[standing] }} />
          {standing === 'better' ? t('f-benchmark-legend-better') : t('f-benchmark-legend-worse')}
        </p>
      )}
    </HoverCard>
  );
};

export interface BenchmarkBarProps {
  metric: FarmBenchmarkMetric;
  standing: FarmBenchmarkStanding;
}

/**
 * The farm's twelve-month figure as a bar from zero, over the other farms' lowest-to-highest band, with
 * their average as a line across it. Hand-drawn rather than recharts: it is three rectangles per row, and
 * each row has its own unit and scale.
 *
 * A farm with no figure draws no bar, but the band and the average still show what the others reached.
 */
export default function BenchmarkBar({ metric, standing }: BenchmarkBarProps) {
  const { t } = useTranslation();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const { anchor, openByPointer, openByFocus, clear } = useAnchorRect(wrapperRef);

  const { farmValue, othersAverage, othersMin, othersMax } = metric;
  const max = Math.max(farmValue ?? 0, othersAverage ?? 0, othersMax ?? 0) * HEADROOM || 1;
  const label = metricLabel(metric.metric, t);

  return (
    // Focus opens the card too: the sample size and the number of farms compared exist nowhere else.
    <div
      ref={wrapperRef}
      role="img"
      tabIndex={0}
      aria-label={t('f-benchmark-bar-label', {
        metric: label,
        farm: farmValue === null ? t('f-benchmark-no-data') : formatValue(farmValue, metric.unit),
        average: othersAverage === null ? t('f-benchmark-no-data') : formatValue(othersAverage, metric.unit)
      })}
      className="relative h-6 w-full rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      onMouseEnter={openByPointer}
      onMouseLeave={clear}
      onFocus={openByFocus}
      onBlur={clear}
    >
      <div className="absolute inset-0 rounded" style={{ backgroundColor: c.track }} />

      {othersMin !== null && othersMax !== null && (
        <div
          className="absolute inset-y-0"
          style={{
            left: percent(othersMin, max),
            width: `calc(${percent(othersMax, max)} - ${percent(othersMin, max)})`,
            backgroundColor: c.range
          }}
        />
      )}

      {farmValue !== null && farmValue > 0 && (
        <div
          className="absolute left-0 top-1/2 h-3 -translate-y-1/2 rounded-r"
          style={{ width: percent(farmValue, max), backgroundColor: c[standing] }}
        />
      )}

      {othersAverage !== null && (
        <div
          className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full"
          style={{ left: percent(othersAverage, max), backgroundColor: c.average }}
        />
      )}

      {anchor && <BarTooltip metric={metric} standing={standing} anchor={anchor} />}
    </div>
  );
}
